// 004 (FR-002, FR-008, FR-049, FR-053; P-PROF-2): My children. Linked rows show
// "Class 5 · Section A · <School>" with Edit and Remove; unlinked rows show "Not linked to a school"
// with Link with school code (US6) and Remove. Add a child opens Add student on the last scanned or
// opened school when a pending join is kept (amendment 2026-10-08), else 003's code step.
// Phones: each child is one tappable row into its screen (where Remove lives), and Add a child is
// the last row.
import { useRouter } from "expo-router";
import { View } from "react-native";

import { addStudentHref } from "@/join/handoff";
import { pendingJoin } from "@/join/pendingJoin";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { useTheme } from "@/theme/useTheme";
import { PlusIcon } from "@/ui/icons/PlusIcon";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { ListRow } from "@/ui/ListRow";
import { Text } from "@/ui/Text";

import { useRemoveChild } from "./children/useRemoveChild";
import { PROFILE_COPY } from "./copy";
import { Avatar, Card, SectionHead, useProfileStyles } from "./ui";
import { canAddChild, type ChildVM } from "./viewModels";

export function ChildrenSection({
  items,
  online,
}: {
  items: readonly ChildVM[];
  online: boolean;
}) {
  const styles = useProfileStyles();
  const theme = useTheme();
  const router = useRouter();
  const remove = useRemoveChild();
  const canAdd = canAddChild(items.length);
  const phone = useChromeKind() === "mobile";
  const addChild = () =>
    void pendingJoin
      .read()
      .then((p) =>
        router.push(
          p ? addStudentHref(p.code, p.source) : "/enter-school-code?mode=add",
        ),
      );

  if (phone) {
    return (
      <Card testID="profile-children">
        <SectionHead
          title={PROFILE_COPY.children}
          subtitle={PROFILE_COPY.childrenSubtitle}
        />
        {items.length === 0 ? (
          <Text tone="muted">{PROFILE_COPY.childrenEmpty}</Text>
        ) : null}
        <View>
          {items.map((c) => (
            <ListRow
              key={c.id}
              testID={`child-${c.id}`}
              leading={<Avatar text={c.initial} />}
              label={c.name}
              detail={[
                c.kind === "linked"
                  ? PROFILE_COPY.childLine(
                      c.classLabel,
                      c.section,
                      c.school.name,
                    )
                  : PROFILE_COPY.notLinked,
                ...(c.kind === "linked" ? [] : [PROFILE_COPY.linkWithCode]),
              ]}
              accessibilityLabel={
                c.kind === "linked"
                  ? PROFILE_COPY.editChild(c.name)
                  : `${PROFILE_COPY.linkWithCode}: ${c.name}`
              }
              chevron
              disabled={!online}
              onPress={() =>
                router.push(
                  c.kind === "linked"
                    ? `/children/${c.id}`
                    : `/children/${c.id}?link=1`,
                )
              }
            />
          ))}
          {canAdd ? (
            <ListRow
              testID="profile-add-child"
              leading={
                <View style={styles.avatar}>
                  <PlusIcon
                    color={theme.color.primaryText}
                    size={theme.size.icon.md}
                  />
                </View>
              }
              label={PROFILE_COPY.addChild}
              tone="action"
              disabled={!online}
              onPress={addChild}
            />
          ) : (
            <Text tone="muted">{PROFILE_COPY.childLimit}</Text>
          )}
        </View>
      </Card>
    );
  }

  return (
    <Card testID="profile-children">
      <SectionHead
        title={PROFILE_COPY.children}
        subtitle={PROFILE_COPY.childrenSubtitle}
        action={
          canAdd ? (
            <LinkButton disabled={!online} onPress={addChild}>
              {PROFILE_COPY.addChild}
            </LinkButton>
          ) : null
        }
      />
      {!canAdd ? <Text tone="muted">{PROFILE_COPY.childLimit}</Text> : null}
      {items.length === 0 ? (
        <Text tone="muted">{PROFILE_COPY.childrenEmpty}</Text>
      ) : null}
      {items.map((c) => (
        <View key={c.id} style={styles.row} testID={`child-${c.id}`}>
          <Avatar text={c.initial} />
          <View style={styles.rowText}>
            <Text weight="semiBold">{c.name}</Text>
            <Text tone="muted">
              {c.kind === "linked"
                ? PROFILE_COPY.childLine(c.classLabel, c.section, c.school.name)
                : PROFILE_COPY.notLinked}
            </Text>
            {c.hasOpenOrder ? (
              <Text tone="muted" size="sm">
                {PROFILE_COPY.openOrderRemove(c.name)}
              </Text>
            ) : null}
            {remove.error?.id === c.id && remove.error.text ? (
              <InlineMessage tone="error">{remove.error.text}</InlineMessage>
            ) : null}
          </View>
          <View style={styles.actions}>
            {c.kind === "linked" ? (
              <LinkButton
                accessibilityLabel={PROFILE_COPY.editChild(c.name)}
                disabled={!online}
                onPress={() => router.push(`/children/${c.id}`)}
              >
                Edit
              </LinkButton>
            ) : (
              <LinkButton
                accessibilityLabel={`${PROFILE_COPY.linkWithCode}: ${c.name}`}
                disabled={!online}
                onPress={() => router.push(`/children/${c.id}?link=1`)}
              >
                {PROFILE_COPY.linkWithCode}
              </LinkButton>
            )}
            <LinkButton
              accessibilityLabel={PROFILE_COPY.removeChild(c.name)}
              disabled={!online || c.hasOpenOrder}
              onPress={() => remove.ask({ id: c.id, name: c.name })}
            >
              {PROFILE_COPY.remove}
            </LinkButton>
          </View>
        </View>
      ))}
      {remove.dialog}
    </Card>
  );
}
